import { useState } from "react"
import { useForm } from "react-hook-form"
import { api, ApiError } from "../../lib/api"
import { useCurrentUser } from "../../contexts/UserContext"

type ProfileFormValues = {
  name: string
  email: string
}

type PasswordFormValues = {
  current_password: string
  password: string
  password_confirmation: string
}

const ProfileTab = () => {
  const { user, updateUser } = useCurrentUser()
  const isOAuthUser = !!user?.provider
  const [profileSuccessMessage, setProfileSuccessMessage] = useState("")

  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    setError: setProfileError,
    formState: { errors: profileErrors, isSubmitting: isProfileSubmitting },
  } = useForm<ProfileFormValues>({
    defaultValues: { name: user?.name ?? "", email: user?.email ?? "" },
  })

  const onSubmitProfile = async (data: ProfileFormValues) => {
    setProfileSuccessMessage("")
    try {
      const res = await api.patch<{ user: { id: number; name: string; email: string } }>(
        "/users/me", { user: data }
      )
      updateUser({ name: res.user.name, email: res.user.email })
      setProfileSuccessMessage("プロフィールを更新しました")
    } catch (err) {
      const { data: errData } = err as ApiError
      setProfileError("root", { message: errData.errors?.[0] ?? "エラーが発生しました" })
    }
  }

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PasswordFormValues>()

  const [successMessage, setSuccessMessage] = useState("")

  const onSubmitPassword = async (data: PasswordFormValues) => {
    setSuccessMessage("")
    try {
      await api.patch("/users", { user: data })
      setSuccessMessage("パスワードを変更しました")
      reset()
    } catch (err) {
      const { data: errData } = err as ApiError
      const message = errData.errors?.[0] ?? "エラーが発生しました"
      setError("root", { message })
    }
  }

  return (
    <div className="settings-card">
      <div className="settings-card__header">
        <i className="bi bi-person-circle" />
        <h2>プロフィール設定</h2>
      </div>
      <div className="settings-card__body">
        {profileErrors.root && (
          <p style={{ fontSize: 13, color: "#ef4444", marginBottom: 12 }}>{profileErrors.root.message}</p>
        )}
        {profileSuccessMessage && (
          <p style={{ fontSize: 13, color: "#16a34a", marginBottom: 12 }}>{profileSuccessMessage}</p>
        )}

        <form onSubmit={handleSubmitProfile(onSubmitProfile)}>
          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="name">表示名</label>
              <input
                id="name"
                type="text"
                placeholder="田中 太郎"
                {...registerProfile("name", { required: "表示名を入力してください" })}
              />
              {profileErrors.name && (
                <p style={{ fontSize: 12, color: "#ef4444", marginTop: 4 }}>{profileErrors.name.message}</p>
              )}
            </div>
            <div className="form-field">
              <label htmlFor="email">メールアドレス</label>
              <input
                id="email"
                type="email"
                placeholder="example@example.com"
                disabled={isOAuthUser}
                {...registerProfile("email", isOAuthUser ? {} : { required: "メールアドレスを入力してください" })}
              />
              {isOAuthUser ? (
                <p style={{ fontSize: 12, color: "#4b5563", marginTop: 4 }}>
                  <i className="bi bi-google" style={{ marginRight: 4 }} />
                  Googleアカウントでログインしているため変更できません
                </p>
              ) : (
                profileErrors.email && (
                  <p style={{ fontSize: 12, color: "#ef4444", marginTop: 4 }}>{profileErrors.email.message}</p>
                )
              )}
            </div>
          </div>

          <div className="settings-card__actions">
            <button type="submit" className="btn-primary" disabled={isProfileSubmitting}>
              <i className="bi bi-check-lg" />
              {isProfileSubmitting ? "保存中..." : "プロフィールを保存"}
            </button>
          </div>
        </form>

        <hr className="settings-divider" />
        <h3 className="settings-section-title">パスワード変更</h3>

        {isOAuthUser ? (
          <p style={{ fontSize: 13, color: "#4b5563" }}>
            <i className="bi bi-google" style={{ marginRight: 4 }} />
            Googleアカウントでログインしているためパスワードは設定されていません
          </p>
        ) : (
          <>
            {errors.root && (
              <p style={{ fontSize: 13, color: "#ef4444", marginBottom: 12 }}>{errors.root.message}</p>
            )}
            {successMessage && (
              <p style={{ fontSize: 13, color: "#16a34a", marginBottom: 12 }}>{successMessage}</p>
            )}

            <form onSubmit={handleSubmit(onSubmitPassword)}>
              <div className="form-grid">
                <div className="form-field">
                  <label htmlFor="current_password">現在のパスワード</label>
                  <input
                    id="current_password"
                    type="password"
                    {...register("current_password", { required: "現在のパスワードを入力してください" })}
                  />
                  {errors.current_password && (
                    <p style={{ fontSize: 12, color: "#ef4444", marginTop: 4 }}>{errors.current_password.message}</p>
                  )}
                </div>
                <div />
                <div className="form-field">
                  <label htmlFor="password">新しいパスワード</label>
                  <input
                    id="password"
                    type="password"
                    {...register("password", {
                      required: "新しいパスワードを入力してください",
                      minLength: { value: 8, message: "パスワードは8文字以上で入力してください" },
                    })}
                  />
                  {errors.password && (
                    <p style={{ fontSize: 12, color: "#ef4444", marginTop: 4 }}>{errors.password.message}</p>
                  )}
                </div>
                <div className="form-field">
                  <label htmlFor="password_confirmation">新しいパスワード（確認）</label>
                  <input
                    id="password_confirmation"
                    type="password"
                    {...register("password_confirmation", {
                      required: "確認用パスワードを入力してください",
                      validate: (v) => v === watch("password") || "パスワードが一致しません",
                    })}
                  />
                  {errors.password_confirmation && (
                    <p style={{ fontSize: 12, color: "#ef4444", marginTop: 4 }}>{errors.password_confirmation.message}</p>
                  )}
                </div>
              </div>

              <div className="settings-card__actions">
                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                  <i className="bi bi-check-lg" />
                  {isSubmitting ? "保存中..." : "変更を保存"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

export default ProfileTab
